<div class="min-h-screen bg-gray-50 dark:bg-slate-900">
  <!-- Header avec actions rapides -->
  <div
    class="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-10"
  >
    <div class="max-w-7xl mx-auto px-6 py-4">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
            🎯 Recommandations de préparation de la semaine
          </h1>
          <p class="text-gray-600 dark:text-gray-400 mt-1">
            Optimisez vos quantités, réduisez le gaspillage et maximisez vos
            marges
          </p>
        </div>

        <!-- Quick Actions -->
        <div class="flex items-center gap-2">
          @for (action of quickActions; track action) {
            <button
              (click)="action.action()"
              class="quick-action-btn px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 flex items-center gap-2"
              [class.bg-blue-50]="
                action.id === 'compare-week' && showComparison
              "
              [class.border-blue-300]="
                action.id === 'compare-week' && showComparison
              "
            >
              <span>{{ action.icon }}</span>
              <span class="hidden sm:inline">{{ action.label }}</span>
            </button>
          }
        </div>
      </div>
    </div>
  </div>

  <div class="max-w-7xl mx-auto px-6 py-6">
    <!-- Loading State -->
    @if (isLoading) {
      <div class="space-y-4">
        <div class="grid grid-cols-4 gap-4">
          @for (_ of [1, 2, 3, 4]; track _) {
            <div
              class="bg-white dark:bg-slate-800 rounded-lg p-4 h-24 skeleton"
            ></div>
          }
        </div>
        @for (_ of [1, 2, 3]; track _) {
          <div
            class="bg-white dark:bg-slate-800 rounded-xl h-40 skeleton"
          ></div>
        }
      </div>
    }

    <!-- Content -->
    @if (!isLoading) {
      <div>
        <!-- Aperçu des alertes (plans sans alertes automatiques, ex. Starter) -->
        @if (
          dayAlerts.length > 0 &&
          !planFeatureService.canAccess(FEATURES.AUTO_ALERTS)
        ) {
          <div class="mb-6">
            <app-alert-preview [alerts]="dayAlerts" />
          </div>
        }
        <!-- Alertes Section — max 4 alertes unifiées (météo + événements + calendrier) -->
        @if (
          dayAlerts.length > 0 &&
          planFeatureService.canAccess(FEATURES.AUTO_ALERTS)
        ) {
          <div class="mb-6 space-y-3">
            @for (alert of dayAlerts; track alert.date + alert.title) {
              <div
                class="rounded-r-lg p-4 animate-slide-in border-l-4"
                [ngClass]="alertService.getAlertClasses(alert)"
              >
                <div class="flex items-start gap-3">
                  <span class="text-2xl">{{ alert.icon }}</span>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <p
                        class="font-semibold"
                        [ngClass]="alertService.getAlertTitleClass(alert)"
                      >
                        {{ alert.title }}
                      </p>
                      <span
                        class="text-xs px-2 py-0.5 rounded-full font-bold"
                        [ngClass]="
                          alertService.getImpactBadgeClass(alert.impactPct)
                        "
                      >
                        {{ alert.impactPct > 0 ? "+" : ""
                        }}{{ alert.impactPct }}%
                      </span>
                      <span
                        class="text-xs px-2 py-0.5 rounded-full font-medium"
                        [ngClass]="
                          alertService.getConfidenceClass(alert.confidence)
                        "
                      >
                        🎯 {{ alert.confidence }}%
                      </span>
                    </div>
                    <button
                      (click)="alert.expanded = !alert.expanded"
                      class="text-xs mt-1 underline cursor-pointer"
                      [ngClass]="alertService.getAlertLinkClass(alert)"
                    >
                      {{ alert.expanded ? "Masquer" : "Voir" }} le détail
                    </button>
                    @if (alert.expanded) {
                      <p
                        class="text-sm mt-2 leading-relaxed"
                        [ngClass]="alertService.getAlertDetailClass(alert)"
                      >
                        {{ alert.detail }}
                      </p>
                      @if (
                        alert.source === "weather" &&
                        alert.hourlySlots &&
                        alert.hourlySlots.length > 0
                      ) {
                        <div class="mt-2 flex gap-2 overflow-x-auto pb-1">
                          @for (slot of alert.hourlySlots; track slot.hour) {
                            <div
                              class="flex-shrink-0 text-center bg-white dark:bg-slate-800 rounded-lg border px-2 py-1.5 min-w-[60px]"
                              [ngClass]="{
                                'border-red-300 bg-red-50 dark:bg-red-900/20':
                                  slot.precipitationProbability > 60,
                                'border-amber-300 bg-amber-50 dark:bg-amber-900/20':
                                  slot.precipitationProbability > 30 &&
                                  slot.precipitationProbability <= 60,
                                'border-amber-200 dark:border-amber-800':
                                  slot.precipitationProbability <= 30,
                              }"
                            >
                              <p
                                class="text-xs font-medium text-gray-700 dark:text-gray-300"
                              >
                                {{ slot.hour }}
                              </p>
                              <p
                                class="text-sm font-bold"
                                [ngClass]="{
                                  'text-red-600 dark:text-red-400':
                                    slot.precipitationProbability > 60,
                                  'text-amber-600':
                                    slot.precipitationProbability > 30 &&
                                    slot.precipitationProbability <= 60,
                                  'text-gray-600 dark:text-gray-400':
                                    slot.precipitationProbability <= 30,
                                }"
                              >
                                {{ slot.temperature }}°
                              </p>
                              <p
                                class="text-[10px] text-gray-500 dark:text-gray-400"
                              >
                                {{ slot.condition }}
                              </p>
                              @if (slot.precipitationProbability > 0) {
                                <p
                                  class="text-[10px] text-blue-600 dark:text-blue-400"
                                >
                                  💧 {{ slot.precipitationProbability }}%
                                </p>
                              }
                            </div>
                          }
                        </div>
                      }
                    }
                  </div>
                  <div class="text-right flex-shrink-0">
                    <span
                      class="text-xs px-2 py-1 rounded block whitespace-nowrap"
                      [ngClass]="alertService.getAlertDateBadgeClass(alert)"
                    >
                      📍 {{ alert.dateLabel }}
                    </span>
                  </div>
                </div>
              </div>
            }
          </div>
        }
        <!-- KPIs Hebdomadaires avec comparaison optionnelle -->
        <div class="grid grid-cols-4 gap-4 mb-6">
          <!-- Services -->
          <div
            class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Services cette semaine
                </p>
                <p
                  class="text-3xl font-bold text-gray-900 dark:text-white mt-1"
                >
                  {{ weeklyRecommendations.length * 2 }}
                </p>
                @if (showComparison && weekComparison) {
                  <div class="mt-1">
                    <span
                      class="trend-badge"
                      [ngClass]="{
                        'trend-positive': weekComparison.items.trend > 0,
                        'trend-negative': weekComparison.items.trend < 0,
                        'trend-neutral': weekComparison.items.trend === 0,
                      }"
                    >
                      {{ getTrendIcon(weekComparison.items.trend) }}
                      {{ weekComparison.items.trend > 0 ? "+" : ""
                      }}{{ weekComparison.items.trend }}%
                    </span>
                  </div>
                }
              </div>
              <div
                class="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center"
              >
                <span class="text-2xl">📅</span>
              </div>
            </div>
          </div>
          <!-- Articles -->
          <div
            class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Articles/semaine
                </p>
                <p
                  class="text-3xl font-bold text-gray-900 dark:text-white mt-1"
                >
                  {{ getWeeklyTotalItems() }}
                </p>
                @if (showComparison && weekComparison) {
                  <div class="mt-1">
                    <span
                      class="trend-badge"
                      [ngClass]="{
                        'trend-positive': weekComparison.items.trend > 0,
                        'trend-negative': weekComparison.items.trend < 0,
                        'trend-neutral': weekComparison.items.trend === 0,
                      }"
                    >
                      {{ getTrendIcon(weekComparison.items.trend) }}
                      {{ weekComparison.items.trend > 0 ? "+" : ""
                      }}{{ weekComparison.items.trend }}%
                    </span>
                  </div>
                }
              </div>
              <div
                class="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center"
              >
                <span class="text-2xl">📦</span>
              </div>
            </div>
          </div>
          <!-- CA estimé -->
          <div
            class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  CA estimé/semaine
                </p>
                <p
                  class="text-3xl font-bold text-gray-900 dark:text-white mt-1"
                >
                  {{ getWeeklyRevenue() }}€
                </p>
                @if (showComparison && weekComparison) {
                  <div class="mt-1">
                    <span
                      class="trend-badge"
                      [ngClass]="{
                        'trend-positive': weekComparison.revenue.trend > 0,
                        'trend-negative': weekComparison.revenue.trend < 0,
                        'trend-neutral': weekComparison.revenue.trend === 0,
                      }"
                    >
                      {{ getTrendIcon(weekComparison.revenue.trend) }}
                      {{ weekComparison.revenue.trend > 0 ? "+" : ""
                      }}{{ weekComparison.revenue.trend }}%
                    </span>
                  </div>
                }
              </div>
              <div
                class="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex items-center justify-center"
              >
                <span class="text-2xl">💰</span>
              </div>
            </div>
          </div>
          <!-- Confiance -->
          <div
            class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Confiance moy.
                </p>
                <p
                  class="text-3xl font-bold text-gray-900 dark:text-white mt-1"
                >
                  {{ getWeeklyConfidence() }}%
                </p>
                @if (showComparison && weekComparison) {
                  <div class="mt-1">
                    <span
                      class="trend-badge"
                      [ngClass]="{
                        'trend-positive': weekComparison.confidence.trend > 0,
                        'trend-negative': weekComparison.confidence.trend < 0,
                        'trend-neutral': weekComparison.confidence.trend === 0,
                      }"
                    >
                      {{ getTrendIcon(weekComparison.confidence.trend) }}
                      {{ weekComparison.confidence.trend > 0 ? "+" : ""
                      }}{{ weekComparison.confidence.trend }}%
                    </span>
                  </div>
                }
              </div>
              <div
                class="w-12 h-12 bg-orange-50 dark:bg-orange-900/20 rounded-lg flex items-center justify-center"
              >
                <span class="text-2xl">⚡</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Jours validés counter -->
        @if (validatedDays.size > 0) {
          <div
            class="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-center justify-between"
          >
            <div class="flex items-center gap-2">
              <span class="text-green-600 dark:text-green-400">✅</span>
              <span class="text-green-800 dark:text-green-300 font-medium"
                >{{ validatedDays.size }}/{{
                  weeklyRecommendations.length
                }}
                jours validés</span
              >
            </div>
            <button
              (click)="validatedDays.clear()"
              class="text-sm text-green-700 dark:text-green-300 hover:text-green-900 underline"
            >
              Réinitialiser
            </button>
          </div>
        }
        <!-- Liste des 7 jours -->
        <div class="space-y-4">
          @for (day of weeklyRecommendations; track day; let i = $index) {
            <div
              class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 border overflow-hidden transition-all"
              [class.border-green-300]="isValidated(day.date)"
              [class.border-gray-200]="!isValidated(day.date)"
              [class.validated-pulse]="isValidated(day.date)"
            >
              <!-- Header jour -->
              <div
                class="p-4 border-b border-gray-200 dark:border-slate-700"
                [class.bg-gradient-to-r]="true"
                [class.from-green-50]="isValidated(day.date)"
                [class.to-emerald-50]="isValidated(day.date)"
                [class.from-blue-50]="!isValidated(day.date)"
                [class.to-indigo-50]="!isValidated(day.date)"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div
                      class="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                      [class.bg-green-600]="isValidated(day.date)"
                      [class.bg-blue-600]="!isValidated(day.date)"
                    >
                      @if (!isValidated(day.date)) {
                        <span>{{ i + 1 }}</span>
                      }
                      @if (isValidated(day.date)) {
                        <span>✓</span>
                      }
                    </div>
                    <div>
                      <h3
                        class="text-lg font-bold text-gray-900 dark:text-white"
                      >
                        {{ day.dayName }} {{ formatShortDate(day.date) }}
                      </h3>
                      <p class="text-sm text-gray-600 dark:text-gray-400">
                        {{ day.services.length }} services
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    @if (!isValidated(day.date)) {
                      <button
                        (click)="validateDay(day.date)"
                        class="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        ✓ Valider
                      </button>
                    }
                    <button
                      (click)="toggleDayDetails(day.date)"
                      class="px-4 py-2 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <span>{{
                        expandedDays[day.date] ? "Masquer" : "Voir détails"
                      }}</span>
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          [attr.d]="
                            expandedDays[day.date]
                              ? 'M5 15l7-7 7 7'
                              : 'M19 9l-7 7-7-7'
                          "
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <!-- Services du jour (aperçu) -->
              <div class="p-4">
                <div class="grid grid-cols-2 gap-4">
                  @for (service of day.services; track service) {
                    <div
                      class="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700"
                    >
                      <div class="flex items-center justify-between mb-3">
                        <div class="flex items-center gap-2">
                          <span class="text-2xl">{{
                            getPeriodIcon(service.period)
                          }}</span>
                          <div>
                            <p
                              class="font-semibold text-gray-900 dark:text-white"
                            >
                              {{ getPeriodLabel(service.period) }}
                            </p>
                            <p class="text-xs text-gray-600 dark:text-gray-400">
                              {{ service.time }}
                            </p>
                          </div>
                        </div>
                        <div class="text-right">
                          <p class="text-xs text-gray-600 dark:text-gray-400">
                            Météo
                          </p>
                          <p class="text-sm font-medium">
                            {{ getWeatherIcon(service.weather.icon) }}
                            {{ service.weather.temp }}°C
                          </p>
                        </div>
                      </div>
                      <div class="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p class="text-xs text-gray-600 dark:text-gray-400">
                            Articles
                          </p>
                          <p class="font-bold text-gray-900 dark:text-white">
                            {{ service.totalItems }}
                          </p>
                        </div>
                        <div>
                          <p class="text-xs text-gray-600 dark:text-gray-400">
                            Valeur
                          </p>
                          <p class="font-bold text-gray-900 dark:text-white">
                            {{ service.totalValue }}€
                          </p>
                        </div>
                        <div>
                          <p class="text-xs text-gray-600 dark:text-gray-400">
                            Confiance
                          </p>
                          <p
                            class="font-bold text-green-600 dark:text-green-400"
                          >
                            {{ service.confidence }}%
                          </p>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>
              <!-- Détails expandables -->
              @if (expandedDays[day.date]) {
                <div
                  class="border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 p-6 animate-slide-in"
                >
                  @for (
                    service of day.services;
                    track service;
                    let sIndex = $index;
                    let isLast = $last
                  ) {
                    <div
                      [class.mb-6]="!isLast"
                      [class.border-b]="!isLast"
                      [class.pb-6]="!isLast"
                      class="border-gray-200 dark:border-slate-700"
                    >
                      <h4
                        class="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
                      >
                        <span>{{ getPeriodIcon(service.period) }}</span>
                        <span
                          >{{ getPeriodLabel(service.period) }} - Détails
                          produits</span
                        >
                      </h4>
                      <div class="space-y-3">
                        @for (
                          product of service.products;
                          track product;
                          let pIndex = $index
                        ) {
                          <div
                            class="bg-white dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700"
                          >
                            <div class="flex items-center gap-4">
                              <div class="text-4xl">{{ product.icon }}</div>
                              <div class="flex-1">
                                <div
                                  class="flex items-center justify-between mb-2"
                                >
                                  <div>
                                    <p
                                      class="font-semibold text-gray-900 dark:text-white"
                                    >
                                      {{ product.name }}
                                    </p>
                                    <p
                                      class="text-xs text-gray-600 dark:text-gray-400"
                                    >
                                      {{ product.category }}
                                    </p>
                                  </div>
                                  <div class="flex items-center gap-2">
                                    <div
                                      class="w-16 h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden"
                                    >
                                      <div
                                        class="h-full bg-green-500"
                                        [style.width.%]="product.confidence"
                                      ></div>
                                    </div>
                                    <span
                                      class="text-xs font-bold text-gray-900 dark:text-white"
                                      >{{ product.confidence }}%</span
                                    >
                                  </div>
                                </div>
                                <div class="grid grid-cols-4 gap-3">
                                  <div
                                    class="text-center bg-gray-50 dark:bg-slate-900 rounded p-2"
                                  >
                                    <p
                                      class="text-xs text-gray-600 dark:text-gray-400"
                                    >
                                      Base
                                    </p>
                                    <p
                                      class="font-bold text-gray-900 dark:text-white"
                                    >
                                      {{ product.baseQty }}
                                    </p>
                                  </div>
                                  <div
                                    class="text-center bg-blue-50 dark:bg-blue-900/20 rounded p-2"
                                  >
                                    <p class="text-xs text-blue-900">✨ IA</p>
                                    <p class="font-bold text-blue-900">
                                      {{ product.recommended }}
                                    </p>
                                  </div>
                                  <div
                                    class="text-center bg-green-50 dark:bg-green-900/20 rounded p-2"
                                  >
                                    <p class="text-xs text-green-900">
                                      Fourchette
                                    </p>
                                    <p class="font-bold text-green-900">
                                      {{ product.low }}-{{ product.high }}
                                    </p>
                                  </div>
                                  <div
                                    class="text-center bg-white dark:bg-slate-800 rounded p-2 border border-gray-200 dark:border-slate-700"
                                  >
                                    <p
                                      class="text-xs text-gray-600 dark:text-gray-400"
                                    >
                                      À préparer
                                    </p>
                                    <input
                                      type="number"
                                      [value]="product.recommended"
                                      [min]="product.low"
                                      [max]="product.high * 2"
                                      (change)="
                                        adjustProductQuantity(
                                          day.date,
                                          sIndex,
                                          pIndex,
                                          $any($event.target).value
                                        )
                                      "
                                      class="w-full text-center font-bold text-gray-900 dark:text-white border-0 bg-transparent focus:ring-1 focus:ring-blue-500 rounded"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }
  </div>

  <!-- Export loading overlay -->
  @if (isExporting) {
    <div
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-xl p-6 flex items-center gap-4"
      >
        <div
          class="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"
        ></div>
        <span class="text-gray-900 dark:text-white font-medium"
          >Génération du PDF...</span
        >
      </div>
    </div>
  }
</div>
